import { getOr } from '@/utils/options';
import Button, { variantFromString } from 'components/src/button';
import Image from 'next/image';
import * as React from 'react';
import { ButtonFieldsFragment } from 'types/generated';
import dynamic from 'next/dynamic';
import successWhite from 'public/lottie/success_white.json';

const Lottie = dynamic(() => import('lottie-react'), { ssr: false });
import { motion } from 'framer-motion';
import typewriter, { Placement, Title } from '@/analytics/segment';
import { fetchContactFormById } from '@/clients/BentoBoxUtils';
import { useSetAtom } from 'jotai/index';
import { callMePrependTextAtom, isCallMeModalOpenAtom, isDoBusinessWithUsFormAtom } from '@/store';

type Props = {
  id?: string | null;
  buttonData: ButtonFieldsFragment;
  autoWidth?: boolean;
  onClick?: () => void;
  animateOnSuccess?: boolean;
  isDisabled?: boolean;
};

const renderIcon = (buttonData: ButtonFieldsFragment, showSuccess: boolean) => {
  if (showSuccess) {
    return (
      <Lottie
        animationData={successWhite}
        loop={false}
        style={{ width: 40, height: 40 }}
        rendererSettings={{
          preserveAspectRatio: 'xMidYMid slice',
        }}
      />
    );
  }

  if (buttonData.icon?.url) {
    return (
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.2 }}>
        <Image className="self-center" src={buttonData.icon.url} alt="" width="24" height="24" />
      </motion.div>
    );
  }

  return null;
};

/**
 * Button component for cards. Note that the onClick is only used if the Button has no link uri.
 * @param param0
 * @returns
 */
const CardButton = ({
  id = '',
  buttonData,
  autoWidth = false,
  onClick,
  animateOnSuccess = true,
  isDisabled = false,
}: Props) => {
  const [showSuccess, setShowSuccess] = React.useState(false);
  const setIsDoBusinessWithUsForm = useSetAtom(isDoBusinessWithUsFormAtom);
  const setIsCallMeModalOpen = useSetAtom(isCallMeModalOpenAtom);
  const setCallMePrependText = useSetAtom(callMePrependTextAtom);

  const handleClick = async () => {
    switch (buttonData.buttonAction) {
      case 'openDoBusinessWithUsModal':
        typewriter.doBusinessWithUsButtonClicked({
          placement: Placement.CardButton,
          title: Title.KomduÍViðskipti,
        });
        const contactForm = await fetchContactFormById('2LFEpvaXWCMfNhrjdQTYFg');
        if (contactForm) {
          setCallMePrependText('');
          setIsDoBusinessWithUsForm(true);
        }
        break;
      case 'callMeModal':
        typewriter.doBusinessWithUsButtonClicked({
          placement: Placement.CardButton,
          title: Title.CallMe,
        });
        setCallMePrependText(buttonData?.leadMessage || '');
        setIsCallMeModalOpen(true);
        break;
      default:
        if (onClick) {
          onClick();
        }
        break;
    }

    if (animateOnSuccess) {
      setShowSuccess(true);
    }
    setTimeout(() => {
      setShowSuccess(false);
    }, 2000);
  };

  if (Boolean(buttonData.uri)) {
    return (
      <a href={getOr(buttonData.uri, '#')} className={autoWidth ? 'w-fit' : 'w-full'}>
        <Button
          // Don't show success animation on Link buttons
          id={id ?? undefined}
          icon={renderIcon(buttonData, false)}
          label={getOr(buttonData.label, '')}
          classes="!px-16"
          autoWidth={autoWidth}
          variant={variantFromString(buttonData.variant)}
          disabled={isDisabled}
        />
      </a>
    );
  }
  return (
    <Button
      id={id ?? undefined}
      icon={renderIcon(buttonData, showSuccess)}
      label={getOr(buttonData.label, '')}
      classes="!px-16"
      autoWidth={autoWidth}
      iconPosition="right"
      variant={variantFromString(buttonData.variant)}
      onClick={handleClick}
      disabled={isDisabled}
    />
  );
};
export default CardButton;
